import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import en from "./translations/en";
import id from "./translations/id";
import { SITE_URL, setHeadValue } from "../utils/seo";

export const LANGUAGES = ["id", "en"];

const DICTIONARIES = { en, id };
const STORAGE_KEY = "purba-language";

// Only the home page is localised in the URL: /id and /en.
const HOME_PATH = /^\/(en|id)\/?$/i;

export const getLanguageFromPath = (pathname) => {
  const match = pathname.match(HOME_PATH);
  return match ? match[1].toLowerCase() : null;
};

const detectInitialLanguage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (LANGUAGES.includes(saved)) return saved;
  } catch (e) {
    // localStorage can be unavailable (private mode, blocked site data)
  }

  const browserLanguages =
    typeof navigator !== "undefined"
      ? navigator.languages || [navigator.language]
      : [];
  const prefersIndonesian = browserLanguages.some(
    (lang) => typeof lang === "string" && lang.toLowerCase().startsWith("id")
  );

  return prefersIndonesian ? "id" : "en";
};

const lookup = (dictionary, key) =>
  key.split(".").reduce((node, part) => node?.[part], dictionary);

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Remembered choice. On the home page the URL wins; everywhere else this is used.
  const [preferred, setPreferred] = useState(detectInitialLanguage);
  // Temporary override for pages that are always shown in one language (see
  // useForcedLanguage). It never touches the remembered choice.
  const [forced, setForced] = useState(null);

  const urlLanguage = getLanguageFromPath(pathname);
  const isHome = urlLanguage !== null;
  const language = forced ?? urlLanguage ?? preferred;

  // Keep the remembered choice in sync when the URL changes the language
  // (toggle, direct link, or the browser back/forward buttons).
  useEffect(() => {
    if (urlLanguage && urlLanguage !== preferred) setPreferred(urlLanguage);
  }, [urlLanguage, preferred]);

  useEffect(() => {
    document.documentElement.lang = language;

    const { title, description, imageAlt, locale } = DICTIONARIES[language].meta;
    document.title = title;
    setHeadValue('meta[name="description"]', "content", description);
    setHeadValue('meta[property="og:title"]', "content", title);
    setHeadValue('meta[property="og:description"]', "content", description);
    setHeadValue('meta[property="og:image:alt"]', "content", imageAlt);
    setHeadValue('meta[property="og:locale"]', "content", locale);
    setHeadValue('meta[name="twitter:title"]', "content", title);
    setHeadValue('meta[name="twitter:description"]', "content", description);
    setHeadValue('meta[name="twitter:image:alt"]', "content", imageAlt);
  }, [language]);

  // Only the visitor's own choice is remembered, never a forced language.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, preferred);
    } catch (e) {
      // ignore: the choice just won't be remembered
    }
  }, [preferred]);

  // Each language of the home page is its own canonical URL.
  useEffect(() => {
    if (!isHome) return;
    const url = `${SITE_URL}/${language}`;
    setHeadValue('link[rel="canonical"]', "href", url);
    setHeadValue('meta[property="og:url"]', "content", url);
  }, [isHome, language]);

  const setLanguage = useCallback(
    (code) => {
      if (!LANGUAGES.includes(code)) return;
      setPreferred(code);
      if (isHome) navigate(`/${code}`);
    },
    [isHome, navigate]
  );

  const t = useCallback(
    (key) => lookup(DICTIONARIES[language], key) ?? lookup(en, key) ?? key,
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      preferredLanguage: preferred,
      setLanguage,
      setForcedLanguage: setForced,
      isHome,
      t,
    }),
    [language, preferred, setLanguage, isHome, t]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used inside <LanguageProvider>");
  }
  return context;
};

// Shows the calling page (navbar and footer included) in a fixed language
// without changing the visitor's remembered language.
export const useForcedLanguage = (code) => {
  const { setForcedLanguage } = useLanguage();

  // Layout effect: applied before paint, so there is no flash of the other language.
  useLayoutEffect(() => {
    setForcedLanguage(code);
    return () => setForcedLanguage(null);
  }, [code, setForcedLanguage]);
};
